import {
  getTheme,
  setTheme as setMagneticTheme,
  Themes
} from "@magnetic/theme";

import { logger } from "src/core/globals";
import { Brig } from "src/gen/rest/brig/BrigService";
import { Fred } from "src/gen/rest/fred/FredService";
import { Theme } from "src/gen/schema/models/auth";
import { UserPreferences } from "src/gen/schema/models/user";

export const Theme2Magnetic: Record<Theme, keyof Themes> = {
  [Theme.THEME_UNSPECIFIED]: "light-classic",
  [Theme.DARK]: "dark-classic",
  [Theme.LIGHT]: "light-classic",
  [Theme.HIGH_CONTRAST]: "light-classic",
  [Theme.UNRECOGNIZED]: "dark-classic"
};

export const Magtic2Theme: Record<keyof Themes, Theme> = {
  light: Theme.LIGHT,
  dark: Theme.DARK,
  "light-classic": Theme.LIGHT,
  "dark-classic": Theme.DARK
};

const THEME = "theme";

/**
 * Retrieve the theme from the
 * local storage so it can be set
 * prior to the application loading
 * user preferences
 */
export function getLocalTheme() {
  /**
   * can't just use window.cnc.settings
   * since for login this will not exist.
   */
  const theme = localStorage.getItem(THEME);
  return Magtic2Theme[theme as keyof Themes];
}

/**
 * Get the system theme preference using the
 * prefers-color-scheme media query. This is used
 * to determine the theme to apply when the "system"
 * theme is received from other products launching HF.
 *
 * system theme should apply the theme based on user's
 * system preferences.
 */
export function getSystemTheme() {
  if (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
  ) {
    return Theme.DARK;
  }
  return Theme.LIGHT;
}

/**
 * Wrapper for the magnetic theme
 * setter that also sets the theme
 * in the local storage.
 */
export function setLocalTheme(
  theme: Theme,
  persistInBackend?: boolean
): Promise<void> {
  const value = Theme2Magnetic[theme];
  /**
   *
   * TORT-6325 Dark mode button incorrectly toggled in Dev cloud
   *
   * window.cnc.settings for THEME must be set every time the theme is set
   * The localstorage needs to get updated any time we set the theme
   * otherwise there is the possibility that it will be out of sync
   * if the user does not update the theme.
   *
   * @Example
   * User1 logs in. User then sets the theme to dark mode using the toggle.
   * User1 logs out. Login screen is now dark mode.
   * User2 goes to login page to login, dark mode is already set from User1
   * User2 logs in, User2 has no existing theme so the theme is the default
   *       light mode from the users preferences.
   * User2 logs out, login is now dark mode because the local storage was never
   *       updated because user 2 never toggled the theme.
   */
  window.cnc.settings.set(THEME, value);

  const root = document.documentElement;
  if (theme === Theme.DARK) {
    root.style.setProperty("--base-bg-default", "#0f1214");
    root.style.setProperty("--shadow-color", "rgba(255, 255, 255, 0.12157");
  } else if (theme === Theme.LIGHT) {
    root.style.setProperty("--base-bg-default", "#f7f7f7");
    root.style.setProperty("--shadow-color", "rgba(0, 0, 0, 0.12157");
  }

  const current = getTheme();
  if (current !== value) {
    setMagneticTheme(value);
  }

  /**
   * Persist the theme in the backend. return
   * a promise so that the caller can wait for
   * the theme to be persisted if necessary.
   * This is the case for C3Login
   */
  if (persistInBackend) {
    return Fred.getUserPreferences()
      .then((preferences: UserPreferences) => {
        // Return the update promise so that caller waits for the backend
        // preference write before proceeding.
        return Brig.updateUserPreferences({
          ...preferences,
          theme
        }).then(() => undefined);
      })
      .catch((err) => {
        logger.error("User Preferences Error: ", err);
      });
  }
  /**
   * If we are not persisting the theme in the backend,
   * return a resolved promise. it will probably be
   * ignored by the caller.
   */
  return Promise.resolve();
}
